import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import InfoCard from 'components/InfoCard/InfoCard';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { StorageE } from 'declarations/enums/storage';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { RoofTopI } from 'declarations/interfaces/roofTop';
import type { TicketCountByRoofTopI } from 'declarations/interfaces/tickets';
import CardLoader from 'pages/TicketsPage/components/CardLoader/CardLoader';
import { CardLoaderWrapStyled } from 'pages/TicketsPage/components/CardLoader/CardLoaderWrapStyled';
import { ReportCardWrapStyled } from 'pages/TicketsPage/TicketsPage.styled';

import { DealerTableWrapStyled, SortIconWrapStyled } from './DealerCard.styled';

type SortColumnT = 'amount' | 'dealer' | 'dealerCount' | 'oem' | 'warrcloud';
type SortOrderT = 'asc' | 'desc';

const COUNT_THRESHOLDS = { low: 0, high: 10 } as const;

const getCountColor = (count: number): string => {
  if (count <= COUNT_THRESHOLDS.low) return '#4CAF50';
  if (count <= COUNT_THRESHOLDS.high) return '#FF9800';
  return '#F44336';
};

const formatAmount = (amount: number): string =>
  `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const DealerCard: FC = (): JSX.Element => {
  const {
    getSearchParam,
    activeTab,
    updateSearchParam,
    clearSearchParam,
    isLoadingTicketsCountByRoofTop,
    mappedRooftopsList,
    roofTopsList,
    dealerSelectedRooftopIds,
    setDealerSelectedRooftopIds,
  } = useTickets();

  const { isMobile, isSmallMobile } = useResponsive();

  const [sortColumn, setSortColumn] = useState<SortColumnT>('dealer');
  const [sortOrder, setSortOrder] = useState<SortOrderT>('asc');
  const [accordionExpanded, setAccordionExpanded] = useState<boolean>((): boolean => {
    const stored = localStorage.getItem(StorageE.DealerCardExpanded);
    return stored !== null ? stored === 'true' : true;
  });

  const selectedRoofTopId = getSearchParam(TicketsRefinementKeysE.RoofTopId);
  const isScheduleTab = activeTab === ActiveTabTypeE.Schedule;
  const selectedDealerRowRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (selectedRoofTopId) {
      if (isMobile && !accordionExpanded) {
        setAccordionExpanded(true);
        localStorage.setItem(StorageE.DealerCardExpanded, 'true');
      }
      const row = selectedDealerRowRef.current;
      if (row) {
        requestAnimationFrame(() => {
          row.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, [selectedRoofTopId, isMobile, accordionExpanded]);

  // --- Dealer refinement dropdown state ---
  const dealerRefineSelectedIds = useMemo((): string[] => {
    if (!dealerSelectedRooftopIds || dealerSelectedRooftopIds.trim() === '') {
      return [];
    }
    return dealerSelectedRooftopIds
      .split(',')
      .map((id): string => id.trim())
      .filter(Boolean);
  }, [dealerSelectedRooftopIds]);

  const [dealerRefineMenuAnchor, setDealerRefineMenuAnchor] = useState<HTMLElement | null>(null);
  const [pendingDealerIds, setPendingDealerIds] = useState<string[] | null>(null);
  const [dealerRefineSearchQuery, setDealerRefineSearchQuery] = useState('');
  const dealerRefineMenuOpen = Boolean(dealerRefineMenuAnchor);

  const handleDealerRefineMenuOpen = (event: MouseEvent<HTMLButtonElement>): void => {
    setPendingDealerIds(dealerRefineSelectedIds.length === 0 ? null : [...dealerRefineSelectedIds]);
    setDealerRefineSearchQuery('');
    setDealerRefineMenuAnchor(event.currentTarget);
  };

  const handleDealerRefineMenuClose = (): void => {
    setDealerRefineMenuAnchor(null);
    setDealerRefineSearchQuery('');
  };

  const dealerRefineFilteredList = useMemo((): RoofTopI[] => {
    const list = roofTopsList ?? [];
    const q = dealerRefineSearchQuery.trim().toLowerCase();
    if (q) return list.filter((r): boolean => (r.name ?? '').toLowerCase().includes(q));
    return list;
  }, [roofTopsList, dealerRefineSearchQuery]);

  const handlePendingToggleAll = (): void => {
    const visibleIds = dealerRefineFilteredList.map((r): string => r.id);
    const allSelected =
      visibleIds.length > 0 &&
      pendingDealerIds !== null &&
      visibleIds.every((id): boolean => pendingDealerIds.includes(id));
    if (allSelected) {
      setPendingDealerIds(null);
    } else {
      setPendingDealerIds(visibleIds.length > 0 ? visibleIds : null);
    }
  };

  const handlePendingToggleDealer = (roofId: string): void => {
    setPendingDealerIds((prev): string[] | null => {
      if (prev === null) return [roofId];
      const next = prev.includes(roofId)
        ? prev.filter((id): boolean => id !== roofId)
        : [...prev, roofId];
      return next.length === 0 ? null : next;
    });
  };

  const handleDealerRefineApply = (): void => {
    setDealerSelectedRooftopIds(pendingDealerIds === null ? null : pendingDealerIds.join(','));
    setDealerRefineMenuAnchor(null);
  };

  const handleDealerRefineReset = (): void => {
    setDealerSelectedRooftopIds(null);
    setPendingDealerIds(null);
    setDealerRefineMenuAnchor(null);
  };

  const dealerRefineCount = dealerRefineSelectedIds.length;

  const isAllDealersInListSelected = useMemo((): boolean => {
    const visibleIds = dealerRefineFilteredList.map((r): string => r.id);
    if (visibleIds.length === 0) return pendingDealerIds === null;
    return (
      pendingDealerIds !== null && visibleIds.every((id): boolean => pendingDealerIds.includes(id))
    );
  }, [dealerRefineFilteredList, pendingDealerIds]);

  // --- End dealer refinement dropdown state ---

  const handleAccordionChange = (_event: React.SyntheticEvent, expanded: boolean): void => {
    setAccordionExpanded(expanded);
    localStorage.setItem(StorageE.DealerCardExpanded, String(expanded));
  };

  const handleSort = (column: SortColumnT): void => {
    if (sortColumn === column) {
      setSortOrder((prev: SortOrderT): SortOrderT => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const handleRowClick = (roofTopId: string): void => {
    if (selectedRoofTopId === roofTopId) {
      clearSearchParam(TicketsRefinementKeysE.RoofTopId);
    } else {
      updateSearchParam(TicketsRefinementKeysE.RoofTopId, roofTopId);
    }
  };

  const handleAllClick = (): void => {
    clearSearchParam(TicketsRefinementKeysE.RoofTopId);
  };

  const sortedRooftops = useMemo((): TicketCountByRoofTopI[] => {
    let list = mappedRooftopsList ?? [];

    if (dealerRefineSelectedIds.length > 0) {
      const idSet = new Set(dealerRefineSelectedIds);
      list = list.filter((d): boolean => idSet.has(d.roofTopId));
    }

    return [...list].sort((a: TicketCountByRoofTopI, b: TicketCountByRoofTopI): number => {
      let comparison = 0;

      switch (sortColumn) {
        case 'dealer':
          comparison = a.roofTopName.localeCompare(b.roofTopName);
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'dealerCount':
          comparison = a.assignedToDealerCount - b.assignedToDealerCount;
          break;
        case 'warrcloud':
          comparison = a.assignedToWCCount - b.assignedToWCCount;
          break;
        case 'oem':
          comparison = a.assignedToOEMCount - b.assignedToOEMCount;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [mappedRooftopsList, sortColumn, sortOrder, dealerRefineSelectedIds]);

  const totals = useMemo((): {
    totalAmount: number;
    assignedToDealerCount: number;
    assignedToWCCount: number;
    assignedToOEMCount: number;
  } => {
    const list = sortedRooftops;
    return list.reduce(
      (
        acc: {
          totalAmount: number;
          assignedToDealerCount: number;
          assignedToWCCount: number;
          assignedToOEMCount: number;
        },
        r: TicketCountByRoofTopI
      ): {
        totalAmount: number;
        assignedToDealerCount: number;
        assignedToWCCount: number;
        assignedToOEMCount: number;
      } => ({
        totalAmount: acc.totalAmount + r.amount,
        assignedToDealerCount: acc.assignedToDealerCount + r.assignedToDealerCount,
        assignedToWCCount: acc.assignedToWCCount + r.assignedToWCCount,
        assignedToOEMCount: acc.assignedToOEMCount + r.assignedToOEMCount,
      }),
      {
        totalAmount: 0,
        assignedToDealerCount: 0,
        assignedToWCCount: 0,
        assignedToOEMCount: 0,
      }
    );
  }, [sortedRooftops]);

  const dealerCount = sortedRooftops.length;

  const renderSortIcon = (column: SortColumnT): JSX.Element | null => {
    if (sortColumn !== column) return null;

    return (
      <SortIconWrapStyled>
        {sortOrder === 'asc' ? (
          <ArrowUpwardIcon sx={{ fontSize: 14 }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 14 }} />
        )}
      </SortIconWrapStyled>
    );
  };

  const renderCountCell = (count: number, bold: boolean): JSX.Element => (
    <Box
      component="span"
      sx={{
        color: getCountColor(count),
        fontWeight: bold ? 600 : 'inherit',
      }}
    >
      {count}
    </Box>
  );

  const dealerRefineDropdown = (
    <>
      <Button
        id="dealer-refine-button"
        aria-haspopup="listbox"
        aria-expanded={dealerRefineMenuOpen}
        onClick={handleDealerRefineMenuOpen}
        variant="outlined"
        size="small"
        sx={{
          minWidth: 120,
          justifyContent: 'space-between',
          fontSize: 12,
          height: 28,
          textTransform: 'none',
        }}
        endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
      >
        Dealers
        {dealerRefineCount > 0 ? (
          <>
            {' '}
            <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>
              {`(${dealerRefineCount})`}
            </Box>
          </>
        ) : null}
      </Button>
      <Menu
        anchorEl={dealerRefineMenuAnchor}
        open={Boolean(dealerRefineMenuAnchor)}
        onClose={handleDealerRefineMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            maxHeight: 380,
            width: 280,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        MenuListProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 0,
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ flexShrink: 0, p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            size="small"
            placeholder="Search dealers"
            value={dealerRefineSearchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>): void =>
              setDealerRefineSearchQuery(e.target.value)
            }
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: dealerRefineSearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear dealer search"
                    size="small"
                    onClick={(): void => setDealerRefineSearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <ListItemButton onClick={handlePendingToggleAll}>
            <ListItemIcon>
              <Checkbox checked={isAllDealersInListSelected} edge="start" disableRipple />
            </ListItemIcon>
            <ListItemText primary="All" />
          </ListItemButton>
          {dealerRefineFilteredList.map(
            (roof): JSX.Element => (
              <ListItemButton
                key={roof.id}
                onClick={(): void => handlePendingToggleDealer(roof.id)}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={pendingDealerIds !== null && pendingDealerIds.includes(roof.id)}
                    edge="start"
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={roof.name} />
              </ListItemButton>
            )
          )}
          {dealerRefineSearchQuery.trim() && dealerRefineFilteredList.length === 0 && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', px: 2, py: 1 }}>
              No dealers match your search.
            </Typography>
          )}
        </List>
        <Box
          sx={{
            flexShrink: 0,
            p: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            display: 'flex',
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleDealerRefineReset}
            sx={{ flex: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleDealerRefineApply}
            sx={{ flex: 1 }}
          >
            Apply
          </Button>
        </Box>
      </Menu>
      <Tooltip
        title="Select dealers to refine the list"
        arrow
        placement="top"
        slotProps={{
          popper: {
            modifiers: [{ name: 'flip', enabled: true }],
            sx: { zIndex: 1300 },
          },
        }}
      >
        <span style={{ display: 'inline-flex', lineHeight: 0 }}>
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: 'action.active', cursor: 'help' }}
            aria-label="Select dealers to refine the list"
          />
        </span>
      </Tooltip>
    </>
  );

  const showNumericColumns = !isScheduleTab && !isSmallMobile;

  const renderTable = (): JSX.Element => (
    <DealerTableWrapStyled>
      <Table size="small" stickyHeader>
        {!isSmallMobile && (
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'nowrap',
                    minWidth: 0,
                    '& > *': { flexShrink: 0 },
                  }}
                >
                  <Box
                    component="span"
                    onClick={(): void => handleSort('dealer')}
                    sx={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontWeight: 700,
                    }}
                  >
                    Dealers{renderSortIcon('dealer')}
                  </Box>
                  {!isSmallMobile && dealerRefineDropdown}
                </Box>
              </TableCell>
              {showNumericColumns && (
                <>
                  <TableCell align="right" onClick={(): void => handleSort('amount')}>
                    Amount{renderSortIcon('amount')}
                  </TableCell>
                  <TableCell align="right" onClick={(): void => handleSort('dealerCount')}>
                    <Tooltip title="Assigned to Dealer">
                      <span>Dealer</span>
                    </Tooltip>
                    {renderSortIcon('dealerCount')}
                  </TableCell>
                  <TableCell align="right" onClick={(): void => handleSort('warrcloud')}>
                    <Tooltip title="Assigned to WarrCloud">
                      <span>WC</span>
                    </Tooltip>
                    {renderSortIcon('warrcloud')}
                  </TableCell>
                  <TableCell align="right" onClick={(): void => handleSort('oem')}>
                    <Tooltip title="Assigned to OEM">
                      <span>OEM</span>
                    </Tooltip>
                    {renderSortIcon('oem')}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          <TableRow
            hover
            selected={!selectedRoofTopId}
            onClick={handleAllClick}
            sx={{ cursor: 'pointer' }}
          >
            <TableCell>
              <Box component="span" sx={{ fontWeight: 600 }}>
                All
              </Box>
            </TableCell>
            {showNumericColumns && (
              <>
                <TableCell align="right">
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {formatAmount(totals.totalAmount)}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {renderCountCell(totals.assignedToDealerCount, true)}
                </TableCell>
                <TableCell align="right">
                  {renderCountCell(totals.assignedToWCCount, true)}
                </TableCell>
                <TableCell align="right">
                  {renderCountCell(totals.assignedToOEMCount, true)}
                </TableCell>
              </>
            )}
          </TableRow>

          {sortedRooftops.map((rooftop: TicketCountByRoofTopI): JSX.Element => {
            const isSelected = selectedRoofTopId === rooftop.roofTopId;

            return (
              <TableRow
                key={rooftop.roofTopId}
                ref={isSelected ? selectedDealerRowRef : undefined}
                hover
                selected={isSelected}
                onClick={(): void => handleRowClick(rooftop.roofTopId)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Tooltip title={rooftop.roofTopName} enterDelay={500}>
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {rooftop.roofTopName}
                    </Box>
                  </Tooltip>
                </TableCell>
                {showNumericColumns && (
                  <>
                    <TableCell align="right">{formatAmount(rooftop.amount)}</TableCell>
                    <TableCell align="right">
                      {renderCountCell(rooftop.assignedToDealerCount, false)}
                    </TableCell>
                    <TableCell align="right">
                      {renderCountCell(rooftop.assignedToWCCount, false)}
                    </TableCell>
                    <TableCell align="right">
                      {renderCountCell(rooftop.assignedToOEMCount, false)}
                    </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </DealerTableWrapStyled>
  );

  if (isLoadingTicketsCountByRoofTop) {
    if (isMobile) {
      return (
        <Accordion expanded={accordionExpanded} onChange={handleAccordionChange}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Dealers</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <CardLoaderWrapStyled>
              <CardLoader />
            </CardLoaderWrapStyled>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (
      <ReportCardWrapStyled>
        <InfoCard>
          <CardLoaderWrapStyled>
            <CardLoader />
          </CardLoaderWrapStyled>
        </InfoCard>
      </ReportCardWrapStyled>
    );
  }

  if (isMobile) {
    return (
      <Accordion expanded={accordionExpanded} onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            Dealers
            {!isSmallMobile && ` (${dealerCount})`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0, overflow: 'auto', maxHeight: '50vh' }}>
          {renderTable()}
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <ReportCardWrapStyled>
      <InfoCard>{renderTable()}</InfoCard>
    </ReportCardWrapStyled>
  );
};

export default DealerCard;
